import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  Cube, CheckCircle, Wrench, Tag, Clock, WarningCircle, Plus, ArrowsLeftRight, Spinner,
} from "@phosphor-icons/react";

const STAT_CARDS = [
  { key: "total", label: "Total Assets", icon: Cube, color: "text-zinc-900", bg: "bg-zinc-100" },
  { key: "available", label: "Available", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "in_service", label: "In Service", icon: Wrench, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "sold", label: "Sold", icon: Tag, color: "text-zinc-600", bg: "bg-zinc-100" },
  { key: "pending", label: "Pending", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  { key: "missing", label: "Missing", icon: WarningCircle, color: "text-red-600", bg: "bg-red-50" },
];

const PIE_COLORS = ["#10B981", "#3B82F6", "#71717A", "#F59E0B", "#EF4444"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === "corporate_manager") { navigate("/app/corporate", { replace: true }); return; }
    api.get("/dashboard").then(({ data }) => setData(data));
  }, [user?.role, navigate]);

  if (!data)
    return <div className="flex items-center justify-center h-96"><Spinner size={32} className="animate-spin text-emerald-600" /></div>;

  const pieData = data.by_status.filter((s) => s.count > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-zinc-500 text-sm mt-1">Overview of your entire asset inventory.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="p-4 rounded-lg border border-zinc-200 bg-white flex flex-col justify-between min-h-[104px]" data-testid={`stat-${c.key}`}>
            <div className="flex items-center gap-2">
              <div className={`h-9 w-9 rounded-md ${c.bg} flex items-center justify-center shrink-0`}>
                <c.icon size={20} weight="bold" className={c.color} />
              </div>
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold leading-tight">{c.label}</p>
            </div>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-3">{data[c.key]}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* By status pie */}
        <div className="p-5 rounded-lg border border-zinc-200 bg-white">
          <h3 className="font-display font-bold mb-4">Assets by Status</h3>
          {pieData.length === 0 ? (
            <p className="text-sm text-zinc-400 py-12 text-center">No assets yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="count" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {pieData.map((s, i) => (
              <span key={s.name} className="flex items-center gap-1.5 text-xs text-zinc-600">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {s.name} ({s.count})
              </span>
            ))}
          </div>
        </div>

        {/* By location bar */}
        <div className="p-5 rounded-lg border border-zinc-200 bg-white lg:col-span-2">
          <h3 className="font-display font-bold mb-4">Assets by Location</h3>
          {data.by_location.every((l) => l.count === 0) ? (
            <p className="text-sm text-zinc-400 py-12 text-center">No assets assigned to locations yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_location.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recently added */}
        <div className="p-5 rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 mb-4"><Plus size={18} weight="bold" className="text-emerald-600" /><h3 className="font-display font-bold">Recently Added</h3></div>
          <div className="space-y-2">
            {data.recent_added.length === 0 && <p className="text-sm text-zinc-400">Nothing yet.</p>}
            {data.recent_added.map((a) => (
              <button key={a.id} onClick={() => navigate(`/app/assets/${a.id}`)} className="w-full text-left flex items-center justify-between p-2 rounded-md hover:bg-zinc-50" data-testid={`recent-added-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{a.year} {a.make} {a.model}</p>
                  <p className="text-xs text-zinc-500">{a.asset_id}</p>
                </div>
                <span className="text-xs text-zinc-400">{a.location_name || "—"}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recently moved */}
        <div className="p-5 rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 mb-4"><ArrowsLeftRight size={18} weight="bold" className="text-emerald-600" /><h3 className="font-display font-bold">Recently Moved</h3></div>
          <div className="space-y-2">
            {data.recent_moved.length === 0 && <p className="text-sm text-zinc-400">No movements yet.</p>}
            {data.recent_moved.map((m, i) => (
              <div key={i} className="flex items-center justify-between p-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{m.label || m.asset_id}</p>
                  <p className="text-xs text-zinc-500">→ {m.to_location}</p>
                </div>
                <span className="text-xs text-zinc-400">{new Date(m.at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="p-5 rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 mb-4"><Clock size={18} weight="bold" className="text-emerald-600" /><h3 className="font-display font-bold">Recent Activity</h3></div>
          <div className="space-y-3">
            {data.recent_activity.length === 0 && <p className="text-sm text-zinc-400">No activity yet.</p>}
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <span className="h-2 w-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <div>
                  <p className="text-zinc-800"><span className="font-semibold">{a.user_name}</span> {a.detail}</p>
                  <p className="text-xs text-zinc-400">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
